// Notes SC6a R4 — the clinical formatting toolbar (decision 9, the mock's order): Undo · Redo | Heading · Bold · Italic ·
// Underline · Highlight | Bulleted list · Numbered list | Clear formatting. Titles / aria-labels from the approved strings.
// Active states (aria-pressed) and disabled states follow the editor on every transaction; pointerdown never takes the
// focus from the editor. 44px targets on phones, 36px on desktop (CSS). The row sits above the scrolling editor, so it
// stays at the top of the clinical pane; on phones, while the editor has focus, it follows the visual viewport (iOS
// keyboard). Logical CSS only; in RTL the row reads right to left (flex order follows the direction).
import { toggleListBySoftLines } from '../../lib/soft-line-lists.js'; // F-64
const H = () => window.HUD || {};

const BTNS = [
  { k: 'undo', label: 'notes_tb_undo', icon: '↶', run: c => c.undo(), can: e => e.can().undo() },
  { k: 'redo', label: 'notes_tb_redo', icon: '↷', run: c => c.redo(), can: e => e.can().redo() },
  { sep: true },
  { k: 'heading', label: 'notes_tb_heading', icon: 'H', run: c => c.toggleHeading({ level: 3 }), active: e => e.isActive('heading') },
  { k: 'bold', label: 'notes_tb_bold', icon: 'B', cls: 'vn2-tb-b', run: c => c.toggleBold(), active: e => e.isActive('bold') },
  { k: 'italic', label: 'notes_tb_italic', icon: 'I', cls: 'vn2-tb-i', run: c => c.toggleItalic(), active: e => e.isActive('italic') },
  { k: 'underline', label: 'notes_tb_underline', icon: 'U', cls: 'vn2-tb-u', run: c => c.toggleUnderline(), active: e => e.isActive('underline') },
  { k: 'highlight', label: 'notes_tb_highlight', icon: '🖍', run: c => c.toggleHighlight(), active: e => e.isActive('highlight') },
  { sep: true },
  { k: 'bullets', label: 'notes_tb_bullets', icon: '•≡', run: (c, e) => toggleListBySoftLines(c, e, 'bulletList'), active: e => e.isActive('bulletList') }, // F-64: one item per line
  { k: 'numbered', label: 'notes_tb_numbered', icon: '1.≡', run: (c, e) => toggleListBySoftLines(c, e, 'orderedList'), active: e => e.isActive('orderedList') },
  { sep: true },
  { k: 'dir', label: 'notes_tb_dir', icon: 'LTR', cls: 'vn2-tb-dir', run: c => c.hudToggleDir() }, // F-55: flips the paragraph(s) at the caret
  { sep: true },
  { k: 'clear', label: 'notes_tb_clear', icon: 'Aa̶', run: c => c.unsetAllMarks().clearNodes() },
];

export function buildRichToolbar(field){
  const { el, T } = H();
  const bar = el('div', { class: 'vn2-tb', role: 'toolbar', 'aria-label': T('notes_tb_label') });
  const btns = [];
  for (const b of BTNS) {
    if (b.sep) { bar.appendChild(el('span', { class: 'vn2-tb-sep', 'aria-hidden': 'true' })); continue; }
    const btn = el('button', { type: 'button', class: 'vn2-tb-btn' + (b.cls ? ' ' + b.cls : ''), title: T(b.label), 'aria-label': T(b.label), 'data-tb': b.k }, [el('span', { 'aria-hidden': 'true' }, [b.icon])]);
    btn.addEventListener('pointerdown', ev => ev.preventDefault()); // keep the editor's focus + selection
    btn.addEventListener('mousedown', ev => ev.preventDefault());
    btn.onclick = () => { const e = field.editor; if (!e || !e.isEditable) return; b.run(e.chain().focus(), e).run(); paint(); };
    if (b.active) btn.setAttribute('aria-pressed', 'false');
    bar.appendChild(btn); btns.push([b, btn]);
  }
  function paint(){
    const e = field.editor, on = !!(e && e.isEditable && !field.disabled && !field.readOnly);
    for (const [b, btn] of btns) {
      btn.disabled = !on || (b.can ? !b.can(e) : false);
      if (b.k === 'dir') { // the label shows the direction the line has now; the tooltip names what a tap does
        const cur = on && e.storage.hudListItemDir ? e.storage.hudListItemDir.current(e) : (document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr');
        btn.firstChild.textContent = cur === 'rtl' ? 'RTL' : 'LTR';
        btn.title = H().T(cur === 'rtl' ? 'notes_tb_dir_ltr' : 'notes_tb_dir_rtl');
      }
      if (b.active) { const a = on && b.active(e); btn.setAttribute('aria-pressed', a ? 'true' : 'false'); btn.classList.toggle('vn2-tb-on', !!a); }
    }
  }
  field.onStateChange = paint; // one slot: a re-rendered toolbar replaces the previous one
  paint();
  _pinOnPhones(bar, field);
  return bar;
}

// Phones: while the editor has focus, keep the toolbar at the top of the VISUAL viewport (the iOS keyboard shrinks it and
// can scroll the layout viewport). A spacer keeps the layout from jumping. Desktop / tablet: plain flow (no-op).
function _pinOnPhones(bar, field){
  const vv = window.visualViewport; if (!vv) return;
  if (field._pinDispose) field._pinDispose(); // a rebuilt toolbar replaces the previous one's listeners at once (the field outlives re-renders of its note)
  let spacer = null, pinned = false, gone = false;
  // VV-LISTENER-LEAK: removal is explicit. field.destroy() (sheet close, note switch) and the next toolbar for this field call it; the old lazy self-removal
  // (only on a later event, and only if the bar had been seen connected) left 3 listeners per open when a sheet closed before any viewport event.
  const dispose = () => {
    if (gone) return; gone = true;
    vv.removeEventListener('resize', apply); vv.removeEventListener('scroll', apply); window.removeEventListener('resize', apply);
    if (pinned) { bar.classList.remove('vn2-tb-pinned'); bar.style.top = ''; if (spacer) spacer.remove(); spacer = null; pinned = false; }
    if (field._pinDispose === dispose) field._pinDispose = null;
    if (field._onFocusChange === apply) field._onFocusChange = null;
  };
  const apply = () => {
    if (gone) return;
    if (!bar.isConnected && !pinned && bar.__hudSeen) { dispose(); return; } // replaced / closed (kept as a second line of defence)
    if (bar.isConnected) bar.__hudSeen = true;
    const vvNow = window.visualViewport || vv;
    // only while the on-screen keyboard is up (the visual viewport is clearly shorter than the layout viewport)
    const kb = vvNow.height < window.innerHeight - 80;
    const want = window.innerWidth < 768 && kb && !!field.editor && field.editor.isFocused && bar.isConnected;
    if (want && !pinned) {
      spacer = document.createElement('div'); spacer.className = 'vn2-tb-spacer'; spacer.style.height = bar.offsetHeight + 'px';
      bar.parentNode && bar.parentNode.insertBefore(spacer, bar); bar.classList.add('vn2-tb-pinned'); pinned = true;
    } else if (!want && pinned) { bar.classList.remove('vn2-tb-pinned'); bar.style.top = ''; if (spacer) spacer.remove(); spacer = null; pinned = false; }
    if (pinned) bar.style.top = Math.max(0, Math.round(vvNow.offsetTop)) + 'px';
  };
  vv.addEventListener('resize', apply); vv.addEventListener('scroll', apply); window.addEventListener('resize', apply);
  bar.__hudPin = apply;
  field._pinDispose = dispose;
  field._onFocusChange = apply;
}
